import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar.tsx";
import { cn } from "@/lib/utils.ts";
import { Plug } from "lucide-react";
import { type ReactNode, useState } from "react";

type ConnectorLogoSize = "sm" | "md" | "lg";

const sizeClasses: Record<ConnectorLogoSize, string> = {
  sm: "h-6 w-6",
  md: "h-7 w-7",
  lg: "h-9 w-9",
};

const iconSizeClasses: Record<ConnectorLogoSize, string> = {
  sm: "h-3.5 w-3.5",
  md: "h-4 w-4",
  lg: "h-5 w-5",
};

export function connectorFaviconUrl(websiteUrl: string): string | null {
  try {
    const hostname = new URL(websiteUrl).hostname;
    return hostname ? `https://icons.duckduckgo.com/ip3/${hostname}.ico` : null;
  } catch {
    return null;
  }
}

/**
 * A connector's mark drawn from the service's own favicon, for the
 * admin-registered long tail we ship no brand SVG for.
 *
 * `fallback` overrides the generic plug for callers that have something better
 * to show. Catalog tiles keep the plug — they are browsing a service, and its
 * initial says nothing. A connected account row passes its initial instead,
 * matching native's `ProviderIcon`, because there the letter at least names the
 * account you already have.
 */
export const ConnectorLogo = ({
  websiteUrl,
  name,
  size = "md",
  className,
  fallback,
}: {
  websiteUrl?: string | null;
  name: string;
  size?: ConnectorLogoSize;
  className?: string;
  fallback?: ReactNode;
}) => {
  const [imageFailed, setImageFailed] = useState(false);
  const faviconSrc = websiteUrl && !imageFailed ? connectorFaviconUrl(websiteUrl) : null;

  return (
    <Avatar className={cn(sizeClasses[size], className)}>
      {faviconSrc && (
        <AvatarImage
          src={faviconSrc}
          alt=""
          aria-hidden="true"
          className="object-cover"
          onLoadingStatusChange={(status) => {
            if (status === "error") setImageFailed(true);
          }}
        />
      )}
      <AvatarFallback aria-label={`${name} connector`} className="bg-muted text-muted-foreground">
        {fallback ?? <Plug className={iconSizeClasses[size]} />}
      </AvatarFallback>
    </Avatar>
  );
};
